import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, X } from "lucide-react";

const empty = {
  name: "", category: "Wellness", short_description: "", description: "",
  price: 0, compare_at_price: 0, image_url: "",
  benefits: "", ingredients: "", how_to_use: "", size: "",
  in_stock: true, featured: false,
};

const toForm = (p) => ({
  ...p,
  benefits: (p.benefits || []).join("\n"),
  ingredients: (p.ingredients || []).join("\n"),
});

const toPayload = (f) => ({
  name: f.name,
  category: f.category,
  short_description: f.short_description,
  description: f.description,
  price: parseFloat(f.price) || 0,
  compare_at_price: parseFloat(f.compare_at_price) || 0,
  image_url: f.image_url,
  gallery: [],
  benefits: f.benefits.split("\n").map((s) => s.trim()).filter(Boolean),
  ingredients: f.ingredients.split("\n").map((s) => s.trim()).filter(Boolean),
  how_to_use: f.how_to_use,
  size: f.size,
  in_stock: !!f.in_stock,
  featured: !!f.featured,
});

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null or product or 'new'
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (p) => { setForm(toForm(p)); setEditing(p); };
  const close = () => { setEditing(null); setForm(empty); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (editing === "new") {
        await api.post("/admin/products", payload);
        toast.success("Product created");
      } else {
        await api.put(`/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      }
      close();
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div className="min-h-screen bg-oshveda-bgAlt" data-testid="admin-dashboard">
      <header className="bg-oshveda-primary text-oshveda-bg">
        <div className="container-x py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-brand">
            <div className="w-9 h-9 rounded-full bg-oshveda-bg p-1 flex items-center justify-center">
              <img src="/oshveda-logo.png" alt="" className="w-full h-full object-contain" />
            </div>
            <span className="font-serif text-xl">Oshveda Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-oshveda-bg/70 hidden sm:inline">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-xs tracking-widest uppercase border border-oshveda-bg/30 rounded-full px-4 py-2 hover:bg-oshveda-bg/10 transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-x py-12">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="label-eyebrow mb-2">Manage</div>
            <h1 className="font-serif text-4xl text-oshveda-ink">Products</h1>
            <p className="text-oshveda-ink2 mt-2">{products.length} product{products.length !== 1 ? "s" : ""} in your catalogue.</p>
          </div>
          <button onClick={openNew} data-testid="admin-add-product" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-oshveda-primary text-oshveda-bg text-sm tracking-widest uppercase hover:bg-oshveda-primaryHover transition-all">
            <Plus size={16} /> Add Product
          </button>
        </div>

        {loading ? (
          <div className="text-center py-20 text-oshveda-muted">Loading…</div>
        ) : (
          <div className="bg-white rounded-2xl border border-oshveda-borderSubtle overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-oshveda-bgAlt text-left">
                <tr>
                  <th className="px-5 py-4 label-eyebrow">Product</th>
                  <th className="px-5 py-4 label-eyebrow hidden md:table-cell">Category</th>
                  <th className="px-5 py-4 label-eyebrow">Price</th>
                  <th className="px-5 py-4 label-eyebrow hidden lg:table-cell">Stock</th>
                  <th className="px-5 py-4 label-eyebrow text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-t border-oshveda-borderSubtle" data-testid={`admin-row-${p.id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <img src={p.image_url} alt="" className="w-12 h-12 rounded-lg object-cover bg-oshveda-bgAlt" />
                        <div>
                          <div className="font-medium text-oshveda-ink">{p.name}</div>
                          <div className="text-xs text-oshveda-muted">{p.size}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-oshveda-ink2">{p.category}</td>
                    <td className="px-5 py-4 text-oshveda-ink">₹{p.price.toFixed(0)}</td>
                    <td className="px-5 py-4 hidden lg:table-cell">
                      <span className={`text-xs tracking-widest uppercase px-3 py-1 rounded-full ${p.in_stock ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"}`}>
                        {p.in_stock ? "In stock" : "Out"}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.id}`} className="p-2 rounded-lg hover:bg-oshveda-bgAlt text-oshveda-ink2"><Pencil size={15} /></button>
                        <button onClick={() => handleDelete(p)} data-testid={`admin-delete-${p.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-700"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="admin-product-modal">
          <div className="bg-white w-full max-w-3xl rounded-3xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-oshveda-borderSubtle px-8 py-5 flex items-center justify-between rounded-t-3xl">
              <h2 className="font-serif text-2xl text-oshveda-ink">{editing === "new" ? "Add new product" : "Edit product"}</h2>
              <button onClick={close} className="p-2 hover:bg-oshveda-bgAlt rounded-lg" data-testid="admin-modal-close"><X size={18} /></button>
            </div>
            <form onSubmit={handleSave} className="p-8 space-y-5">
              <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="form-name" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Category" required value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="form-category" />
                <Field label="Size / Pack" value={form.size} onChange={(v) => setForm({ ...form, size: v })} testid="form-size" />
              </div>
              <Field label="Short description" required value={form.short_description} onChange={(v) => setForm({ ...form, short_description: v })} testid="form-short-description" />
              <Field label="Full description" type="textarea" rows={4} required value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="form-description" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Price (₹)" type="number" required value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="form-price" />
                <Field label="MRP / Compare at (₹)" type="number" value={form.compare_at_price} onChange={(v) => setForm({ ...form, compare_at_price: v })} testid="form-compare-price" />
              </div>
              <Field label="Image URL" required value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testid="form-image-url" />
              <Field label="Benefits (one per line)" type="textarea" rows={4} value={form.benefits} onChange={(v) => setForm({ ...form, benefits: v })} testid="form-benefits" />
              <Field label="Ingredients (one per line)" type="textarea" rows={3} value={form.ingredients} onChange={(v) => setForm({ ...form, ingredients: v })} testid="form-ingredients" />
              <Field label="How to use" type="textarea" rows={3} value={form.how_to_use} onChange={(v) => setForm({ ...form, how_to_use: v })} testid="form-how-to-use" />
              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 text-sm text-oshveda-ink2">
                  <input type="checkbox" checked={form.in_stock} onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} data-testid="form-in-stock" />
                  In stock
                </label>
                <label className="flex items-center gap-2 text-sm text-oshveda-ink2">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="form-featured" />
                  Featured / Bestseller
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-oshveda-borderSubtle">
                <button type="button" onClick={close} className="px-6 py-3 rounded-full border border-oshveda-borderStrong text-sm tracking-widest uppercase text-oshveda-ink2 hover:bg-oshveda-bgAlt transition-colors">Cancel</button>
                <button type="submit" disabled={saving} data-testid="form-submit" className="px-8 py-3 rounded-full bg-oshveda-primary text-oshveda-bg text-sm tracking-widest uppercase hover:bg-oshveda-primaryHover disabled:opacity-60 transition-colors">
                  {saving ? "Saving…" : (editing === "new" ? "Create" : "Save changes")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, rows = 3, testid }) => (
  <label className="block">
    <span className="label-eyebrow block mb-2">{label}{required && <span className="text-oshveda-accent"> *</span>}</span>
    {type === "textarea" ? (
      <textarea
        rows={rows} required={required} value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-4 py-3 bg-oshveda-bgAlt rounded-xl border border-transparent focus:outline-none focus:border-oshveda-primary transition-colors resize-none"
      />
    ) : (
      <input
        type={type} required={required} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-4 py-3 bg-oshveda-bgAlt rounded-xl border border-transparent focus:outline-none focus:border-oshveda-primary transition-colors"
      />
    )}
  </label>
);
